import { createContext, useContext, useEffect, useState } from 'react'
import { CarService } from 'lib/api'
import { useResErrors, useStateCallback } from 'hooks'
import { NEW_SEO } from 'routes/constants.routes'
import { useHomeStoreManager } from 'context/HomeStore/Manager'
import { deepCopyObject } from 'lib/utils'
import { useAuthStore } from 'context/AuthStore'

const INIT_FILTER = {
  minPrice: null,
  maxPrice: null,
  minMileage: null,
  maxMileage: null,
  minYear: null,
  maxYear: null,
  minEngineCapacity: null,
  maxEngineCapacity: null,
  branchCodes: [],
  markaNew: null,
  modelNew: null,
  keyNumber: null,
  oneCId: null,
  transmission: null,
  bodyTypeCodes: [],
  dwheelTypeCodes: [],
  engineTypeCodes: [],
  steering: null,
  colorCodes: [],
  hasPromo2: false,
  hasPromo1: false,
  statusCodes: ['available', 'com_register']
}

const FilterStoreContext = createContext({
  dictionary: {},
  store: {},
  func: {}
})

export const FilterStoreProvider = ({
  step,
  updateSale,
  prevFilter,
  saleId,
  ...props
}) => {
  const { standardCheck } = useResErrors()
  const {
    homeStore: { branchList }
  } = useHomeStoreManager()
  const {
    authStore: { user }
  } = useAuthStore()

  const [loading, setLoading] = useState(false)
  const [carsList, setCarsList] = useState([])
  const [pagination, setPagination] = useState({
    countFiltered: 0,
    offset: 0
  })
  const [filter, setFilter] = useStateCallback(prevFilter || INIT_FILTER)
  const [dictionary, setDictionary] = useState({
    brands: [],
    models: [],
    bodyTypes: [],
    colors: [],
    engineTypes: [],
    dwheelTypes: []
  })
  const userBranch = user?.data?.branch?.code

  useEffect(() => {
    getDictionary()
  }, [])
  useEffect(() => {
    if (saleId) {
      setFilter(prev => ({ ...prev, branchCodes: [userBranch] }))
      if (prevFilter) {
        setFilter(
          {
            ...INIT_FILTER,
            ...prevFilter,
            branchCodes:
              prevFilter?.branchCodes?.length > 0
                ? prevFilter?.branchCodes.includes(userBranch)
                  ? prevFilter?.branchCodes
                  : [...prevFilter?.branchCodes, userBranch]
                : [userBranch]
          },
          state => getCarsList(state)()
        )
      }
    }
  }, [saleId])
  useEffect(() => {
    if (step !== NEW_SEO && +step === 0) {
      getCarsList()()
    }
    return () => {}
  }, [step])

  const getDictionary = async () => {
    await CarService.getRuBrands().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, brands: res.data }))
      })
    )
    await CarService.getBodyTypes().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, bodyTypes: res.data }))
      })
    )
    await CarService.getColors().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, colors: res.data }))
      })
    )
    await CarService.getDwheelTypes().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, dwheelTypes: res.data }))
      })
    )
    await CarService.getEngineTypes().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, engineTypes: res.data }))
      })
    )
  }

  const getRuModels = async marka => {
    await CarService.getRuModels(marka).then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({ ...prevState, models: res.data }))
      })
    )
  }

  const getCarsList =
    (filterD = filter, offset) =>
    async () => {
      const data = Object.keys(filterD).reduce((acc, key) => {
        if (Array.isArray(filterD[key])) {
          if (filterD[key].length > 0) return { ...acc, [key]: filterD[key] }

          return acc
        }
        if (
          filterD[key] !== undefined &&
          filterD[key] !== null &&
          filterD[key] !== '' &&
          !!filterD[key]
        ) {
          return { ...acc, [key]: filterD[key] }
        }
        return acc
      }, {})
      saleId &&
        !offset &&
        +step !== 0 &&
        updateSale({
          filteredValue:
            Object.keys(data)?.length > 0
              ? JSON.stringify(data)
              : JSON.stringify({ ...INIT_FILTER, branchCodes: [userBranch] })
        }) 
      await CarService.getCarList(
        {
          ...data,
          statusCodes: filter.statusCodes,
          notForSale: false,
          new: false
        },
        offset
      ).then(res =>
        standardCheck(res, () => {
          setPagination({
            countFiltered: res.data?.countFiltered,
            offset: typeof offset === 'number' ? offset : 0
          })
          setCarsList(prev =>
            typeof offset === 'number'
              ? offset === 0
                ? res.data?.listCars
                : [...prev, ...res.data?.listCars]
              : res.data?.listCars
          )
        })
      )
    }
  const onChangeFilter = (value = {}) => {
    setFilter(p => ({
      ...p,
      ...value
    }))
    if (value?.hasOwnProperty('markaNew')) {
      if (value.markaNew) {
        getRuModels(value.markaNew)
      } else {
        setDictionary(prevState => ({ ...prevState, models: [] }))
      }
    }
  }
  const onChangeFilterChip = (value = {}) => {
    setFilter(
      p => ({
        ...p,
        ...value
      }),
      async state => {
        if (value?.hasOwnProperty('markaNew')) {
          await getRuModels(value.markaNew)
        } else {
          setDictionary(prevState => ({ ...prevState, models: [] }))
        }
        getCarsList(state)()
      }
    )
  }
  const onResetFilter = () => {
    setFilter(
      deepCopyObject({ ...INIT_FILTER, branchCodes: [userBranch] }),
      state => getCarsList(state)()
    )
  }
  const fetchMoreData = async () => {
    const offset = pagination.offset + 20
    setPagination(prev => ({ ...prev, offset }))
    await getCarsList(undefined, offset)()
  }

  const store = {
    carsList,
    filter,
    loading,
    countFiltered: pagination.countFiltered
  }
  const func = {
    fetchMoreData,
    onChangeFilter,
    onChangeFilterChip,
    onSearch: getCarsList(filter),
    onResetFilter
  }

  return (
    <FilterStoreContext.Provider
      value={{
        dictionary: { ...dictionary, branchList },
        store,
        func
      }}
    >
      {props.children}
    </FilterStoreContext.Provider>
  )
}
export const useFilterStore = () => useContext(FilterStoreContext)
